import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMatches } from '../../../shared/hooks/useMatches';
import { useMobile } from '../../../shared/hooks/useMobile';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import ScoreAppHeader from '../../../shared/components/layout/ScoreAppHeader';
import Button from '../../../shared/components/ui/Button';
import Toast from '../../../shared/components/ui/Toast';
import { matchPublishingService } from '../../../shared/services/matchPublishingService';
import { ApiKeyService } from '../../../shared/services/apiKeyService';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { getByOrganisationId } from '../../control-room/services/organisationApiConfigService';
import { supabase } from '../../../shared/lib/supabase';

// SEO Tab Component with Inline Publish Form
const SEOTab: React.FC<{ 
  matchId: string; 
  matchName: string; 
  currentMatch: any;
  onSavingChange?: (saving: boolean) => void;
}> = ({ matchId, matchName, currentMatch, onSavingChange }) => {
  const [seoData, setSeoData] = useState({
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    public_slug: '',
    custom_url: ''
  });
  const [saving, setSaving] = useState(false);
  const [originalData, setOriginalData] = useState({
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    public_slug: '',
    custom_url: ''
  });
  const [publishToPublic, setPublishToPublic] = useState(false);
  const [originalPublishState, setOriginalPublishState] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    seo_title?: string;
    seo_description?: string;
    public_slug?: string;
  }>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Load existing SEO data when component mounts
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        console.log('Loading existing data for matchId:', matchId);
        const existingStatus = await matchPublishingService.getMatchPublishingStatus(matchId);
        console.log('Existing status from DB:', existingStatus);
        
        if (existingStatus) {
          const loadedData = {
            seo_title: existingStatus.seo_title || '',
            seo_description: existingStatus.seo_description || '',
            seo_keywords: existingStatus.seo_keywords || '',
            public_slug: existingStatus.public_slug || '',
            custom_url: existingStatus.custom_url || ''
          };
          console.log('Setting loaded data:', loadedData);
          console.log('About to call setSeoData with:', loadedData);
          setSeoData(loadedData);
          setOriginalData(loadedData);
          
          // Load publish state
          const publishState = existingStatus.is_published || false;
          setPublishToPublic(publishState);
          setOriginalPublishState(publishState);
          
          console.log('setSeoData called');
        } else {
          console.log('No existing data found, using defaults');
          const defaultData = {
            seo_title: '',
            seo_description: '',
            seo_keywords: '',
            public_slug: '',
            custom_url: ''
          };
          console.log('Setting default data:', defaultData);
          setSeoData(defaultData);
          setOriginalData(defaultData);
        }
      } catch (error) {
        console.error('Error loading existing SEO data:', error);
      }
    };

    loadExistingData();
  }, [matchId]);

  // Track changes in real-time
  useEffect(() => {
    const dataChanges = (
      seoData.seo_title !== originalData.seo_title ||
      seoData.seo_description !== originalData.seo_description ||
      seoData.seo_keywords !== originalData.seo_keywords ||
      seoData.public_slug !== originalData.public_slug ||
      seoData.custom_url !== originalData.custom_url
    );
    const publishChanges = publishToPublic !== originalPublishState;
    setHasChanges(dataChanges || publishChanges);
  }, [seoData, originalData, publishToPublic, originalPublishState]);


  // Notify parent of saving state changes
  useEffect(() => {
    if (onSavingChange) {
      onSavingChange(saving);
    }
  }, [saving, onSavingChange]);

  const handleInputChange = (field: string, value: string) => {
    setSeoData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field as keyof typeof validationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = () => {
    const errors: typeof validationErrors = {};
    
    if (!seoData.seo_title.trim()) {
      errors.seo_title = 'SEO Title is required';
    }
    
    if (!seoData.seo_description.trim()) {
      errors.seo_description = 'SEO Description is required';
    }
    
    if (!seoData.public_slug.trim()) {
      errors.public_slug = 'Public Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(seoData.public_slug)) {
      errors.public_slug = 'Public Slug must contain only lowercase letters, numbers, and hyphens';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = useCallback(async () => {
    // Check if there are any changes first
    if (!hasChanges) {
      const event = new CustomEvent('show-toast', {
        detail: { type: 'info', message: 'No changes to save.' }
      });
      window.dispatchEvent(event);
      return;
    }

    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      
      // Save SEO data to database
      await matchPublishingService.updateMatchSEOData(matchId, seoData);
      
      // Update publish state separately
      await matchPublishingService.toggleMatchPublishingStatus(matchId, publishToPublic);
      
      // Update original states to reflect saved state
      setOriginalData(seoData);
      setOriginalPublishState(publishToPublic);
      
      // Show success toast
      const event = new CustomEvent('show-toast', {
        detail: { type: 'success', message: 'SEO data saved successfully!' }
      });
      window.dispatchEvent(event);
      
    } catch (error: any) {
      console.error('Error saving SEO data:', error);
      
      // Show error toast
      const event = new CustomEvent('show-toast', {
        detail: { type: 'error', message: 'Error saving SEO data. Please try again.' }
      });
      window.dispatchEvent(event);
    } finally {
      setSaving(false);
    }
  }, [seoData, originalData, hasChanges, matchId]);

  // Add event listener for save button
  useEffect(() => {
    const handleSaveEvent = () => {
      handleSave();
    };

    const seoTabElement = document.querySelector('[data-seo-tab]');
    if (seoTabElement) {
      seoTabElement.addEventListener('save-seo-data', handleSaveEvent);
    }

    return () => {
      if (seoTabElement) {
        seoTabElement.removeEventListener('save-seo-data', handleSaveEvent);
      }
    };
  }, [handleSave]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-seo-tab>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">SEO Settings</h2>
        <p className="text-sm text-gray-600 mt-1">Configure the SEO settings for this match before publishing to the public.</p>
      </div>

      {/* SEO Form */}
      <div className="space-y-6">
            {/* SEO Title */}
            <div>
              <label htmlFor="seo_title" className="block text-sm font-medium text-gray-700 mb-2">
                SEO Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="seo_title"
                value={seoData.seo_title}
                onChange={(e) => handleInputChange('seo_title', e.target.value)}
                placeholder="e.g., Arsenal vs Barcelona 2-1 | Match Report"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.seo_title 
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300'
                }`}
              />
              {validationErrors.seo_title ? (
                <p className="text-xs text-red-500 mt-1">{validationErrors.seo_title}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">This will be used for both search engines and social media sharing</p>
              )}
            </div>

            {/* SEO Description */}
            <div>
              <label htmlFor="seo_description" className="block text-sm font-medium text-gray-700 mb-2">
                SEO Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="seo_description"
                value={seoData.seo_description}
                onChange={(e) => handleInputChange('seo_description', e.target.value)}
                placeholder="e.g., Complete match report, stats, and highlights from Arsenal vs Barcelona"
                rows={3}
                maxLength={500}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.seo_description 
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300'
                }`}
              />
              <div className="flex justify-between items-center mt-1">
                {validationErrors.seo_description ? (
                  <p className="text-xs text-red-500">{validationErrors.seo_description}</p>
                ) : (
                  <p className="text-xs text-gray-500">Complete match report, stats, and highlights</p>
                )}
                <span className="text-xs text-gray-400">{seoData.seo_description.length}/500 characters</span>
              </div>
            </div>

            {/* SEO Keywords */}
            <div>
              <label htmlFor="seo_keywords" className="block text-sm font-medium text-gray-700 mb-2">
                SEO Keywords
              </label>
              <input
                type="text"
                id="seo_keywords"
                value={seoData.seo_keywords}
                onChange={(e) => handleInputChange('seo_keywords', e.target.value)}
                placeholder="e.g., Arsenal, Barcelona, football, match report, stats, highlights"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Comma-separated keywords for better search visibility</p>
            </div>

            {/* Public Slug */}
            <div>
              <label htmlFor="public_slug" className="block text-sm font-medium text-gray-700 mb-2">
                Public Slug <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="public_slug"
                value={seoData.public_slug}
                onChange={(e) => handleInputChange('public_slug', e.target.value)}
                placeholder="e.g., arsenal-vs-barcelona-sep-25-2025"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.public_slug 
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300'
                }`}
              />
              {validationErrors.public_slug ? (
                <p className="text-xs text-red-500 mt-1">{validationErrors.public_slug}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">URL-friendly slug for the match (lowercase, hyphens only)</p>
              )}
            </div>

            {/* Custom URL */}
            <div>
              <label htmlFor="custom_url" className="block text-sm font-medium text-gray-700 mb-2">
                Custom URL <span className="text-gray-400">(Optional)</span>
              </label>
              <input
                type="text"
                id="custom_url"
                value={seoData.custom_url}
                onChange={(e) => handleInputChange('custom_url', e.target.value)}
                placeholder="e.g., champions-league-final-2025"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Override the auto-generated URL slug</p>
            </div>

            {/* Publish Toggle */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Publish Match to Public</h3>
                  <p className="text-xs text-gray-600">Enable public access to the match with configured sections</p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`text-sm font-medium ${!publishToPublic ? 'text-gray-900' : 'text-gray-500'}`}>
                    OFF
                  </span>
                  <button
                    onClick={() => setPublishToPublic(!publishToPublic)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
                      publishToPublic ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        publishToPublic ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className={`text-sm font-medium ${publishToPublic ? 'text-gray-900' : 'text-gray-500'}`}>
                    ON
                  </span>
                </div>
              </div>
            </div>
          </div>
    </div>
  );
};

// Match Center Tab Component with API Integration
const MatchCenterTab: React.FC<{ 
  matchId: string; 
  onSavingChange?: (saving: boolean) => void;
}> = ({ matchId, onSavingChange }) => {
  const location = useLocation();
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  const [matchData, setMatchData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** When 404 from get-match-by-slug (match not published), we still show config but no preview */
  const [previewUnavailableReason, setPreviewUnavailableReason] = useState<string | null>(null);
  const [publicSlug, setPublicSlug] = useState<string | null>(null);
  const [slugLoading, setSlugLoading] = useState(true);
  const [sectionToggles, setSectionToggles] = useState({
    details: false,
    lineups: false,
    timeline: false,
    commentary: false,
    stats: false,
  });
  const [originalToggles, setOriginalToggles] = useState({
    details: false,
    lineups: false,
    timeline: false,
    commentary: false,
    stats: false,
  });
  const [publishToPublic, setPublishToPublic] = useState(false);
  const [originalPublishState, setOriginalPublishState] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    details: false,
    lineups: false,
    timeline: false,
    commentary: false,
    stats: false,
  });

  // Fetch public slug from match publishing data
  useEffect(() => {
    const fetchPublicSlug = async () => {
      try {
        setSlugLoading(true);
        const publishingStatus = await matchPublishingService.getMatchPublishingStatus(matchId);
        setPublicSlug(publishingStatus?.public_slug || null);

        // Load existing match center configuration
        if (publishingStatus) {
          const newToggles = {
            details: publishingStatus.details || false,
            lineups: publishingStatus.lineup || false,
            timeline: publishingStatus.timeline || false,
            commentary: publishingStatus.commentary || false,
            stats: publishingStatus.stats || false,
          };
          const newPublishState = publishingStatus.is_published || false;

          setSectionToggles(newToggles);
          setOriginalToggles(newToggles);
          setPublishToPublic(newPublishState);
          setOriginalPublishState(newPublishState);
        }
      } catch (err) {
        console.error('Error fetching public slug:', err);
        setPublicSlug(null);
      } finally {
        setSlugLoading(false);
      }
    };

    fetchPublicSlug();
  }, [matchId]);

  // Add a refresh function that can be called when returning from SEO tab
  const refreshSlugData = useCallback(async () => {
    try {
      setSlugLoading(true);
      const publishingStatus = await matchPublishingService.getMatchPublishingStatus(matchId);
      setPublicSlug(publishingStatus?.public_slug || null);

      // Load existing match center configuration
      if (publishingStatus) {
        const newToggles = {
          details: publishingStatus.details || false,
          lineups: publishingStatus.lineup || false,
          timeline: publishingStatus.timeline || false,
          commentary: publishingStatus.commentary || false,
          stats: publishingStatus.stats || false,
        };
        const newPublishState = publishingStatus.is_published || false;

        setSectionToggles(newToggles);
        setOriginalToggles(newToggles);
        setPublishToPublic(newPublishState);
        setOriginalPublishState(newPublishState);
      }
    } catch (err) {
      console.error('Error refreshing public slug:', err);
    } finally {
      setSlugLoading(false);
    }
  }, [matchId]);

  // Track changes in toggles and publish state (publish is kept for save payload; API tab owns publish UI)
  useEffect(() => {
    const toggleChanges = Object.keys(sectionToggles).some(
      key => sectionToggles[key as keyof typeof sectionToggles] !== originalToggles[key as keyof typeof originalToggles]
    );
    const publishChanges = publishToPublic !== originalPublishState;
    setHasChanges(toggleChanges || publishChanges);
  }, [sectionToggles, originalToggles, publishToPublic, originalPublishState]);

  // Notify parent of saving state changes
  useEffect(() => {
    if (onSavingChange) {
      onSavingChange(saving);
    }
  }, [saving, onSavingChange]);

  // Save match center configuration
  const handleSave = useCallback(async () => {
    if (!hasChanges) {
      const event = new CustomEvent('show-toast', {
        detail: { type: 'info', message: 'No changes to save.' }
      });
      window.dispatchEvent(event);
      return;
    }

    try {
      setSaving(true);
      
      // Save the toggle configuration and publish state to the match publishing service
      const configData = {
        is_published: publishToPublic,
        details: sectionToggles.details,
        lineup: sectionToggles.lineups,
        timeline: sectionToggles.timeline,
        commentary: sectionToggles.commentary,
        stats: sectionToggles.stats
      };
      
      console.log('Saving match center config:', configData);
      
      // Save to database (section toggles + is_published pass-through; API tab owns publish/processing status)
      await matchPublishingService.updateMatchCenterConfig(matchId, configData);

      if (publishToPublic) {
        setMatchDataRefetchTrigger((t) => t + 1);
      }

      setOriginalToggles(sectionToggles);
      setOriginalPublishState(publishToPublic);
      
      // Show success toast
      const event = new CustomEvent('show-toast', {
        detail: { type: 'success', message: 'Match center configuration saved successfully!' }
      });
      window.dispatchEvent(event);
      
    } catch (error: any) {
      console.error('Error saving match center config:', error);
      
      // Show error toast
      const event = new CustomEvent('show-toast', {
        detail: { type: 'error', message: 'Error saving configuration. Please try again.' }
      });
      window.dispatchEvent(event);
    } finally {
      setSaving(false);
    }
  }, [sectionToggles, publishToPublic, hasChanges, matchId]);

  // Add event listener for save button
  useEffect(() => {
    const handleSaveEvent = () => {
      handleSave();
    };

    const matchCenterElement = document.querySelector('[data-match-center-tab]');
    if (matchCenterElement) {
      matchCenterElement.addEventListener('save-match-center-data', handleSaveEvent);
    }

    return () => {
      if (matchCenterElement) {
        matchCenterElement.removeEventListener('save-match-center-data', handleSaveEvent);
      }
    };
  }, [handleSave]);

  // Listen for tab changes to refresh data when coming back to Match Center
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#match-center') {
        // Refresh slug data when returning to Match Center
        refreshSlugData();
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [refreshSlugData]);

  // Trigger refetch after saving with Publish turned ON (so preview loads without full reload)
  const [matchDataRefetchTrigger, setMatchDataRefetchTrigger] = useState(0);

  // Fetch match data using dynamic slug with API key authentication
  useEffect(() => {
    const fetchMatchData = async () => {
      if (!publicSlug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setPreviewUnavailableReason(null);
        setError(null);

        // Check if organization ID is available
        if (!orgIdFromPath) {
          setPreviewUnavailableReason('config_error');
          setError('Organization ID not found in URL');
          setMatchData(null);
          setLoading(false);
          return;
        }

        // Get API key for the organization from URL path (needed for get-match-by-slug)
        const apiKeyResult = await ApiKeyService.getApiKeyForOrganization(orgIdFromPath);
        if (!apiKeyResult.data) {
          setPreviewUnavailableReason('api_key_unavailable');
          setError(apiKeyResult.error || 'Failed to get API key for organization');
          setMatchData(null);
          setLoading(false);
          return;
        }

        const baseUrl = (process.env.REACT_APP_SUPABASE_URL || 'https://api.kridah.com').replace(/\/$/, '');
        const response = await fetch(`${baseUrl}/functions/v1/get-match-by-slug/${publicSlug}`, {
          headers: {
            'x-api-key': apiKeyResult.data.api_key,
            'Content-Type': 'application/json',
          },
        });

        if (response.status === 404) {
          // Match not found or not published — get-match-by-slug only returns published matches
          setPreviewUnavailableReason('unpublished');
          setError(null);
          setMatchData(null);
          setLoading(false);
          return;
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to fetch match data: ${response.statusText}`);
        }

        const data = await response.json();
        setMatchData(data);
        setPreviewUnavailableReason(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load match data';
        setError(message);
        // Still show config when fetch fails (network, 500, etc.) so user can change toggles/status
        setPreviewUnavailableReason('fetch_error');
      } finally {
        setLoading(false);
      }
    };

    if (!slugLoading) {
      fetchMatchData();
    }
  }, [publicSlug, slugLoading, orgIdFromPath, matchDataRefetchTrigger]);

  // Toggle section visibility
  const handleToggle = (section: string) => {
    setSectionToggles(prev => ({
      ...prev,
      [section]: !prev[section as keyof typeof prev]
    }));
  };

  // Toggle section expansion
  const handleSectionExpansion = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section as keyof typeof prev]
    }));
  };

  // Render JSON section with toggle and accordion
  const renderSection = (title: string, data: any, sectionKey: string) => {
    const isEnabled = sectionToggles[sectionKey as keyof typeof sectionToggles];
    const isExpanded = expandedSections[sectionKey as keyof typeof expandedSections];
    
    return (
      <div key={sectionKey} className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <div className="flex items-center space-x-3">
            <span className={`text-sm font-medium ${isEnabled ? 'text-green-600' : 'text-gray-500'}`}>
              {isEnabled ? 'Included' : 'Excluded'}
            </span>
            <button
              onClick={() => handleToggle(sectionKey)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isEnabled ? 'bg-green-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
        
        {/* Accordion Header */}
        <div className="px-4 py-2 border-b border-gray-100">
          <button
            onClick={() => handleSectionExpansion(sectionKey)}
            className="w-full flex items-center justify-between text-left text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none"
          >
            <span>View JSON Data</span>
            <svg
              className={`w-4 h-4 transform transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : 'rotate-0'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        
        {/* Accordion Content */}
        {isExpanded && (
          <div className="p-4">
            <pre className="bg-gray-50 rounded-lg p-4 text-sm overflow-x-auto text-gray-800 whitespace-pre-wrap">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  };

  if (slugLoading || loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">
            {slugLoading ? 'Loading SEO configuration...' : 'Loading match data...'}
          </div>
        </div>
      </div>
    );
  }

  // Show fallback UI when no public slug is available
  if (!publicSlug) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-4">
            <svg className="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">SEO Configuration Required</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Before you can view match center data, please configure the SEO settings with a valid public slug. 
            This is required for generating the public match URL.
          </p>
          <div className="space-y-4">
            <div className="bg-blue-50 rounded-lg p-4 text-left max-w-md mx-auto">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Next Steps:</h4>
              <ol className="text-sm text-blue-800 space-y-1">
                <li>1. Go to the <strong>SEO tab</strong></li>
                <li>2. Fill in the required SEO fields</li>
                <li>3. Add a <strong>Public Slug</strong></li>
                <li>4. Save the SEO configuration</li>
                <li>5. Return to Match Center to view data</li>
              </ol>
            </div>
            <button
              onClick={() => {
                // Navigate to SEO tab by updating the hash
                window.location.hash = 'seo';
                // Also trigger the tab change programmatically
                window.dispatchEvent(new Event('hashchange'));
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Configure SEO Settings
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error && !previewUnavailableReason) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
            <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Match Data</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            There was an issue loading the match center data. This usually happens when the SEO configuration is incomplete or the public slug is invalid.
          </p>
          <div className="space-y-4">
            <div className="bg-blue-50 rounded-lg p-4 text-left max-w-md mx-auto">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Troubleshooting Steps:</h4>
              <ol className="text-sm text-blue-800 space-y-1">
                <li>1. Go to the <strong>SEO tab</strong></li>
                <li>2. Verify all required SEO fields are filled</li>
                <li>3. Check that the <strong>Public Slug</strong> is valid</li>
                <li>4. Save the SEO configuration</li>
                <li>5. Return to Match Center to reload data</li>
              </ol>
            </div>
            <div className="flex space-x-3 justify-center">
              <button
                onClick={() => {
                  window.location.hash = 'seo';
                  window.dispatchEvent(new Event('hashchange'));
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Configure SEO Settings
              </button>
              <button
                onClick={() => {
                  window.location.reload();
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No match data and not "preview unavailable" (e.g. loading failed with 500 or network)
  if (!matchData && !previewUnavailableReason) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">No match data available</div>
        </div>
      </div>
    );
  }

  // When preview is unavailable (e.g. match not published), show config only — no section previews
  const showPreviewUnavailable = !!previewUnavailableReason;

  return (
    <div className="space-y-6" data-match-center-tab>
      {/* Header with global controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Match Center Configuration</h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSectionToggles(prev => 
                Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: true }), {} as typeof prev)
              )}
              className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200"
            >
              Enable All
            </button>
            <button
              onClick={() => setSectionToggles(prev => 
                Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {} as typeof prev)
              )}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Disable All
            </button>
          </div>
        </div>
        
        <p className="text-gray-600 pt-2">
          Configure which sections should be included in the public API response. 
          Toggle sections to control visibility in the dynamic match pages.
        </p>
      </div>

      {showPreviewUnavailable && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          {previewUnavailableReason === 'unpublished' && (
            <>
              <p className="text-sm text-amber-800">
                <strong>Match data preview is only available after the match is published.</strong> Turn ON &quot;Publish match&quot; in the <strong>API</strong> tab and Save, then return here (or refresh) to load the preview.
              </p>
              <p className="text-xs text-amber-700 mt-2">
                You can still change section toggles here and save; the preview will appear once the match is published from the API tab.
              </p>
            </>
          )}
          {previewUnavailableReason === 'api_key_unavailable' && (
            <>
              <p className="text-sm text-amber-800">
                <strong>Preview unavailable:</strong> Could not load API key for this organization. {error ? `(${error})` : ''}
              </p>
              <p className="text-xs text-amber-700 mt-2">
                You can still change section toggles here and Save. Publish and API status are in the <strong>API</strong> tab. To see the match data preview, ensure your organisation has an active API key (Control Room → API Keys). If you see 406 in the console, check Supabase RLS and that the <code className="bg-amber-100 px-1 rounded">api_keys</code> table is readable by your role.
              </p>
            </>
          )}
          {(previewUnavailableReason === 'config_error' || previewUnavailableReason === 'fetch_error') && (
            <>
              <p className="text-sm text-amber-800">
                <strong>Preview unavailable:</strong> {error || 'Could not load match data.'}
              </p>
              <p className="text-xs text-amber-700 mt-2">
                You can still change section toggles here and Save. Publish and API status are in the <strong>API</strong> tab.
              </p>
            </>
          )}
        </div>
      )}

      {/* Section toggles — always visible so you can turn on/off what get-match-by-slug returns */}
      {/* When preview is unavailable, placeholder data is shown in the accordion; toggles still save. */}
      {/* 1. Details Section */}
      {renderSection('Details', matchData ? {
        match_information: {
          name: matchData.match?.name,
          start_date: matchData.match?.start_date,
          end_date: matchData.match?.end_date,
          venue: matchData.match?.venue,
          home_score: matchData.match?.home_score,
          away_score: matchData.match?.away_score,
          status: matchData.match?.status
        },
        tournament: matchData.match?.tournament,
        season: matchData.match?.season,
        officials: matchData.match?.officials,
        score_summary: matchData.match?.score_summary
      } : { _preview: 'Preview unavailable — publish match or fix API key/whitelist to load.' }, 'details')}

      {/* 2. Lineups Section */}
      {renderSection('Lineups', matchData ? {
        home_team: matchData.match?.home_team,
        away_team: matchData.match?.away_team
      } : { _preview: 'Preview unavailable — publish match or fix API key/whitelist to load.' }, 'lineups')}

      {/* 3. Timeline Section */}
      {renderSection('Timeline', matchData ? matchData.key_events : { _preview: 'Preview unavailable — publish match or fix API key/whitelist to load.' }, 'timeline')}

      {/* 4. Commentary Section */}
      {renderSection('Commentary', matchData ? {
        message: "Commentary feature coming soon...",
        available: false
      } : { _preview: 'Preview unavailable — publish match or fix API key/whitelist to load.' }, 'commentary')}

      {/* 5. Stats Section */}
      {renderSection('Stats', matchData ? matchData.statistics : { _preview: 'Preview unavailable — publish match or fix API key/whitelist to load.' }, 'stats')}

    </div>
  );
};

// API Tab: Publish match and API processing status (no SEO required to publish)
const APITab: React.FC<{
  matchId: string;
  orgId: string;
  onSavingChange?: (saving: boolean) => void;
}> = ({ matchId, orgId, onSavingChange }) => {
  const [publishToPublic, setPublishToPublic] = useState(false);
  const [originalPublishState, setOriginalPublishState] = useState(false);
  type ProcessingStatus = 'pending' | 'analyzing' | 'cancelled' | 'error' | 'completed' | 'deleted' | 'success';
  const statusNotes: Record<ProcessingStatus, string> = {
    pending: 'Processing has not started yet',
    analyzing: 'The match is currently being analyzed',
    cancelled: 'Processing was cancelled',
    error: 'Processing failed and has stopped',
    completed: 'All processing and social media uploads are completed',
    deleted: 'Match data and assets have been deleted',
    success: 'Core processing completed; social media uploads pending',
  };
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>('pending');
  const [originalProcessingStatus, setOriginalProcessingStatus] = useState<ProcessingStatus>('pending');
  const [submissionPayload, setSubmissionPayload] = useState<unknown | null>(null);
  const [payloadExpanded, setPayloadExpanded] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [copiedVideoUrl, setCopiedVideoUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detectedVideos, setDetectedVideos] = useState<Array<{ path: string; url: string }>>([]);
  const [uploadingVideos, setUploadingVideos] = useState<Set<string>>(new Set());
  const [uploadStatus, setUploadStatus] = useState<Record<string, { status: 'success' | 'error'; message: string }>>({});
  const [detectionDebugInfo, setDetectionDebugInfo] = useState<{
    checkedPaths: Array<{ path: string; value: any; isPresent: boolean; isValid: boolean; reason?: string }>;
    hasPayload: boolean;
  } | null>(null);

  // Helper function to get value at JSON path
  const getValueAtPath = (obj: any, path: string): any => {
    const parts = path.split(/[\.\[\]]/).filter(p => p);
    let current = obj;
    for (const part of parts) {
      if (current === null || current === undefined) return null;
      current = current[part];
    }
    return current;
  };

  // Helper function to check if a string is a valid video URL
  const isValidVideoUrl = (url: string): boolean => {
    if (typeof url !== 'string') return false;
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      // Reject video player URLs
      if (hostname.includes('youtube.com') || hostname.includes('youtu.be') || hostname.includes('vimeo.com')) {
        return false;
      }
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Detect video URLs in payload
  const detectVideos = useCallback(async (payload: unknown) => {
    if (!payload || typeof payload !== 'object') {
      setDetectedVideos([]);
      setDetectionDebugInfo({ checkedPaths: [], hasPayload: false });
      return;
    }

    try {
      // Get organization config for video detection paths
      const orgConfig = await getByOrganisationId(orgId);
      const paths = orgConfig?.video_detection_field_paths || [
        'matchVideo',
        'videoUrl',
        'video_url',
        'match_video',
        'videos[0].url',
        'videos[0].videoUrl',
      ];

      const videos: Array<{ path: string; url: string }> = [];
      const foundPaths = new Set<string>();
      const checkedPaths: Array<{ path: string; value: any; isPresent: boolean; isValid: boolean; reason?: string }> = [];

      // Check configured paths first
      for (const path of paths) {
        const value = getValueAtPath(payload, path);
        const isPresent = !(value === null || value === undefined);
        let isValid = false;
        let reason: string | undefined;

        if (!isPresent) {
          reason = 'Field not found in payload';
        } else if (typeof value !== 'string') {
          reason = `Value is not a string (type: ${typeof value})`;
        } else if (value.trim().length === 0) {
          reason = 'Key found but value is empty';
        } else {
          const normalizedValue = value.trim();
          const urlValidation = isValidVideoUrl(normalizedValue);
          if (urlValidation) {
            isValid = true;
            if (!foundPaths.has(normalizedValue)) {
              videos.push({ path, url: normalizedValue });
              foundPaths.add(normalizedValue);
            }
          } else {
            reason = 'Value is not a valid video URL';
          }
        }

        checkedPaths.push({ path, value, isPresent, isValid, reason });
      }

      // Only configured detection paths should be evaluated/displayed.
      // Do not auto-scan the payload for arbitrary URLs.

      setDetectedVideos(videos);
      setDetectionDebugInfo({ checkedPaths, hasPayload: true });
    } catch (err) {
      console.error('Error detecting videos:', err);
      setDetectedVideos([]);
      setDetectionDebugInfo(null);
    }
  }, [orgId]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const pub = await matchPublishingService.getMatchPublishingStatus(matchId);
        if (pub) {
          const isPublished = !!pub.is_published;
          const validStatuses = ['pending', 'analyzing', 'cancelled', 'error', 'completed', 'deleted', 'success'] as const;
          const status = pub.processing_status && validStatuses.includes(pub.processing_status)
            ? pub.processing_status
            : 'pending';
          setPublishToPublic(isPublished);
          setOriginalPublishState(isPublished);
          setProcessingStatus(status);
          setOriginalProcessingStatus(status);
          let parsedPayload: unknown = null;
          if (pub.submission_payload) {
            if (typeof pub.submission_payload === 'string') {
              try {
                parsedPayload = JSON.parse(pub.submission_payload);
              } catch {
                parsedPayload = pub.submission_payload;
              }
            } else {
              parsedPayload = pub.submission_payload;
            }
          }
          setSubmissionPayload(parsedPayload);
          // Detect videos after payload is set
          if (parsedPayload) {
            await detectVideos(parsedPayload);
          } else {
            setDetectedVideos([]);
          }
        }
      } catch (err) {
        console.error('Error loading API tab:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [matchId, detectVideos]);

  useEffect(() => {
    if (onSavingChange) onSavingChange(saving);
  }, [saving, onSavingChange]);

  const hasChanges = publishToPublic !== originalPublishState || processingStatus !== originalProcessingStatus;
  const currentMatchNote = statusNotes[processingStatus];

  const handleSave = useCallback(async () => {
    if (!hasChanges) {
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { type: 'info', message: 'No changes to save.' } }));
      return;
    }
    try {
      setSaving(true);
      await matchPublishingService.toggleMatchPublishingStatus(matchId, publishToPublic);
      const statusUpdated = await matchPublishingService.updateProcessingStatus(matchId, processingStatus);
      if (!statusUpdated) {
        throw new Error('Failed to update processing status. Please verify allowed status values in database constraints.');
      }
      setOriginalPublishState(publishToPublic);
      setOriginalProcessingStatus(processingStatus);
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { type: 'success', message: 'API settings saved.' } }));
    } catch (err: any) {
      console.error('Error saving API tab:', err);
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { type: 'error', message: err?.message || 'Failed to save.' } }));
    } finally {
      setSaving(false);
    }
  }, [matchId, publishToPublic, processingStatus, hasChanges]);

  const handleCopyPayload = useCallback(async () => {
    if (!submissionPayload) return;
    const textToCopy =
      typeof submissionPayload === 'string'
        ? submissionPayload
        : JSON.stringify(submissionPayload, null, 2);
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 1500);
    } catch (err) {
      console.error('Failed to copy submission payload:', err);
      setCopyStatus('failed');
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  }, [submissionPayload]);

  const handleCopyVideoUrl = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedVideoUrl(url);
      setTimeout(() => setCopiedVideoUrl(null), 1500);
    } catch (err) {
      console.error('Failed to copy video URL:', err);
    }
  }, []);

  const handleUploadVideo = useCallback(async (videoUrl: string, path: string) => {
    if (uploadingVideos.has(videoUrl)) return;

    setUploadingVideos(prev => new Set(prev).add(videoUrl));
    setUploadStatus(prev => ({ ...prev, [videoUrl]: { status: 'error', message: '' } }));

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.refreshSession();
      const currentSession = session ?? (await supabase.auth.getSession()).data?.session;
      if (sessionError || !currentSession?.access_token) {
        throw new Error('Not authenticated');
      }
      const token = currentSession.access_token;
      const baseUrl = (process.env.REACT_APP_SUPABASE_URL || 'https://api.kridah.com').replace(/\/$/, '');
      let response: Response;
      try {
        response = await fetch(`${baseUrl}/functions/v1/cms-upload-match-video`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Access-Token': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            matchId,
            sourceUrl: videoUrl,
            quality: 'fullhd',
            accessToken: token,
          }),
        });
      } catch (networkErr: any) {
        throw new Error(networkErr?.message?.includes('fetch') ? 'Network error. Check CORS and connectivity.' : (networkErr?.message || 'Failed to upload video'));
      }

      const text = await response.text();
      if (!response.ok) {
        const errorData = text ? (() => { try { return JSON.parse(text); } catch { return { error: text }; }})() : { error: 'Unknown error' };
        // Surface code/detail from edge function for debugging (missing_token vs invalid_token)
        const msg = [errorData.error, errorData.code, errorData.detail].filter(Boolean).join(' — ');
        throw new Error(msg || 'Failed to upload video');
      }
      const result = text ? JSON.parse(text) : {};
      setUploadStatus(prev => ({
        ...prev,
        [videoUrl]: { status: 'success', message: `Video upload initiated successfully. Video ID: ${result.videoId}` },
      }));
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { type: 'success', message: 'Video upload initiated successfully. The video has been added to your Capture app.' },
      }));
    } catch (err: any) {
      console.error('Error uploading video:', err);
      setUploadStatus(prev => ({
        ...prev,
        [videoUrl]: { status: 'error', message: err?.message || 'Failed to upload video' },
      }));
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { type: 'error', message: err?.message || 'Failed to upload video' },
      }));
    } finally {
      setUploadingVideos(prev => {
        const next = new Set(prev);
        next.delete(videoUrl);
        return next;
      });
    }
  }, [matchId, uploadingVideos]);

  useEffect(() => {
    const el = document.querySelector('[data-api-tab]');
    if (!el) return;
    const onSave = () => handleSave();
    el.addEventListener('save-api-data', onSave);
    return () => el.removeEventListener('save-api-data', onSave);
  }, [handleSave]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex items-center justify-center h-48">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-api-tab>
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h2 className="text-xl font-bold text-gray-900 mb-4">API</h2>
        <p className="text-sm text-gray-600 mb-6">
          Publish this match and control what clients see when they poll Get Unified Stats. You can publish without filling SEO details.
        </p>

        <div className="space-y-6">
          <div className="flex items-center justify-between py-3 border-t border-gray-200 first:border-t-0 first:pt-0">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Publish match</h3>
              <p className="text-xs text-gray-600">Make this match available to clients (List Published Matches, Get Unified Stats).</p>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`text-sm font-medium ${!publishToPublic ? 'text-gray-900' : 'text-gray-500'}`}>OFF</span>
              <button
                type="button"
                onClick={() => setPublishToPublic(!publishToPublic)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
                  publishToPublic ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                  publishToPublic ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
              <span className={`text-sm font-medium ${publishToPublic ? 'text-gray-900' : 'text-gray-500'}`}>ON</span>
            </div>
          </div>

          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <div>
              <h3 className="text-sm font-medium text-gray-900">API processing status</h3>
              <p className="text-xs text-gray-600">Status returned to clients when they poll Get Unified Stats.</p>
            </div>
            <select
              value={processingStatus}
              onChange={(e) => setProcessingStatus(e.target.value as ProcessingStatus)}
              className="mt-1 block w-40 rounded-md border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="pending">Pending</option>
              <option value="analyzing">Analyzing</option>
              <option value="cancelled">Cancelled</option>
              <option value="error">Error</option>
              <option value="completed">Completed</option>
              <option value="deleted">Deleted</option>
              <option value="success">Success</option>
            </select>
          </div>

          <div className="py-3 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900">Match note (read-only)</h3>
            <p className="text-xs text-gray-600 mb-2">This note is returned in API responses as <code>matchNotes</code>.</p>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {currentMatchNote}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-900">Submission payload</h3>
            <p className="text-xs text-gray-600 mb-3">Raw payload submitted by the client when creating this match.</p>
            {submissionPayload ? (
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                  <button
                    type="button"
                    onClick={() => setPayloadExpanded(!payloadExpanded)}
                    className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none"
                  >
                    <span className="mr-2">{payloadExpanded ? 'Hide payload' : 'View payload'}</span>
                    <svg
                      className={`w-4 h-4 transform transition-transform duration-200 ${
                        payloadExpanded ? 'rotate-180' : 'rotate-0'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyPayload}
                    className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {copyStatus === 'copied' ? 'Copied' : copyStatus === 'failed' ? 'Copy failed' : 'Copy'}
                  </button>
                </div>
                {payloadExpanded && (
                  <pre className="bg-gray-50 rounded-b-lg p-4 text-sm overflow-x-auto text-gray-800 whitespace-pre-wrap">
                    {typeof submissionPayload === 'string'
                      ? submissionPayload
                      : JSON.stringify(submissionPayload, null, 2)}
                  </pre>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-500">No submission payload found for this match.</div>
            )}
          </div>

          {/* Detected Videos Section */}
          {detectedVideos.length > 0 ? (
            <div className="border-t border-gray-200 pt-4">
              <details open className="group">
                <summary className="cursor-pointer list-none">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Detected Video URLs</h3>
                      <p className="text-xs text-gray-600 mt-1">
                        {detectedVideos.length} video URL{detectedVideos.length !== 1 ? 's' : ''} found in submission payload
                      </p>
                    </div>
                    <svg
                      className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </summary>
                <div className="mt-3 space-y-2">
                  {detectionDebugInfo?.checkedPaths.some((check) => check.reason === 'Key found but value is empty') && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
                      <div className="font-medium mb-1">Configured video key found but empty</div>
                      {detectionDebugInfo.checkedPaths
                        .filter((check) => check.reason === 'Key found but value is empty')
                        .map((check, idx) => (
                          <div key={`${check.path}-${idx}`}>
                            <code className="bg-white px-1 rounded">{check.path}</code> is present in payload but has empty value.
                          </div>
                        ))}
                    </div>
                  )}
                  {detectedVideos.map((video, index) => {
                    const isUploading = uploadingVideos.has(video.url);
                    const status = uploadStatus[video.url];
                    return (
                      <div
                        key={`${video.url}-${index}`}
                        className="bg-gray-50 rounded-lg border border-gray-200 p-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-700 mb-1">
                              Found in: <code className="bg-white px-1 rounded">{video.path}</code>
                            </div>
                            <div className="text-sm text-gray-900 break-all mb-2">{video.url}</div>
                            {status && (
                              <div className={`text-xs ${status.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                {status.status === 'success' && (
                                  <div className="space-y-1">
                                    <div>{status.message}</div>
                                    <div className="text-gray-600 mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                                      <strong>Note:</strong> This video has been added to your Capture app. All further operations (retries, status tracking, etc.) should be done from the Capture app, not from CMS.
                                    </div>
                                  </div>
                                )}
                                {status.status === 'error' && <div>{status.message}</div>}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => handleCopyVideoUrl(video.url)}
                              className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-nowrap"
                            >
                              {copiedVideoUrl === video.url ? 'Copied' : 'Copy link'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUploadVideo(video.url, video.path)}
                              disabled
                              title="Temporarily disabled"
                              className="px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                                bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-nowrap"
                            >
                              {isUploading ? 'Uploading...' : status?.status === 'success' ? 'Uploaded' : 'Upload & Link to Match'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </details>
            </div>
          ) : detectionDebugInfo && detectionDebugInfo.hasPayload && detectionDebugInfo.checkedPaths.length > 0 ? (
            <div className="border-t border-gray-200 pt-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-yellow-900 mb-2">Video Detection Status</h3>
                <p className="text-xs text-yellow-800 mb-3">
                  No video URLs were detected in the submission payload. The system checked the configured field paths, but none contained valid video URLs.
                </p>
                <details className="mt-2">
                  <summary className="text-xs font-medium text-yellow-900 cursor-pointer hover:text-yellow-700">
                    Show detection details
                  </summary>
                  <div className="mt-2 space-y-2">
                    {detectionDebugInfo.checkedPaths.map((check, idx) => (
                      <div key={idx} className="bg-white rounded border border-yellow-300 p-2 text-xs">
                        <div className="font-medium text-gray-900 mb-1">
                          Path: <code className="bg-gray-100 px-1 rounded">{check.path}</code>
                        </div>
                        {check.isValid ? (
                          <div className="text-green-700">✓ Valid video URL found</div>
                        ) : (
                          <div className="space-y-1">
                            <div className="text-gray-700">
                              Value: <code className="bg-gray-100 px-1 rounded break-all">
                                {typeof check.value === 'string' 
                                  ? check.value.length > 100 
                                    ? `${check.value.substring(0, 100)}...` 
                                    : check.value
                                  : JSON.stringify(check.value)}
                              </code>
                            </div>
                            <div className="text-red-700">
                              ✗ {check.reason || 'Not a valid video URL'}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
                <p className="text-xs text-yellow-700 mt-3">
                  <strong>Tip:</strong> Make sure your payload contains a field with a valid video URL (e.g., Google Drive direct download link, Dropbox link, or any HTTPS URL). The field name should match one of your configured video detection field paths.
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const InsightsTab: React.FC = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm text-center">
    <div className="text-gray-500 text-lg">Insights</div>
    <div className="text-gray-400 text-sm mt-2">Content coming soon...</div>
  </div>
);

const GalleryTab: React.FC = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm text-center">
    <div className="text-gray-500 text-lg">Gallery</div>
    <div className="text-gray-400 text-sm mt-2">Content coming soon...</div>
  </div>
);

const MatchDetailPage: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMobile();
  const [activeTab, setActiveTab] = useState<'seo' | 'match-center' | 'api' | 'insights' | 'gallery'>('seo');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{isVisible: boolean, type: 'success' | 'error' | 'info', message: string}>({
    isVisible: false,
    type: 'success',
    message: ''
  });
  
  // Extract organization ID from URL for proper navigation
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  
  const { matches, loading: matchesLoading } = useMatches();
  const currentMatch = matches?.find(match => match.id === matchId);

  // Handle hash routing
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['seo', 'match-center', 'api', 'insights', 'gallery'].includes(hash)) {
      setActiveTab(hash as any);
    }
  }, [location.hash]);

  // Add toast event listener
  useEffect(() => {
    const handleToastEvent = (event: CustomEvent) => {
      setToast({
        isVisible: true,
        type: event.detail.type,
        message: event.detail.message
      });
    };

    window.addEventListener('show-toast', handleToastEvent as any);
    
    return () => {
      window.removeEventListener('show-toast', handleToastEvent as any);
    };
  }, []);

  useEffect(() => {
    if (!matchId) {
      navigate(`/o/${orgIdFromPath}/cms`);
      return;
    }
  }, [matchId, navigate, orgIdFromPath]);

  if (matchesLoading) {
    return <LoadingSpinner />;
  }

  if (!currentMatch) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="text-red-600 mb-2">Match not found</div>
          <div className="text-sm text-gray-500">The requested match could not be found.</div>
        </div>
      </div>
    );
  }

  const handleTabChange = (tabId: 'seo' | 'match-center' | 'api' | 'insights' | 'gallery') => {
    setActiveTab(tabId);
    navigate(`#${tabId}`, { replace: true });
  };

  const handleBack = () => {
    navigate(`/o/${orgIdFromPath}/cms`);
  };

  // Tab configuration
  const tabs = [
    { id: 'api', label: 'API', icon: '🔌' },
    { id: 'seo', label: 'SEO', icon: '🔍' },
    { id: 'match-center', label: 'Match Center', icon: '⚽' },
    { id: 'insights', label: 'Insights', icon: '📊' },
    { id: 'gallery', label: 'Gallery', icon: '🖼️' }
  ] as const;

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'seo':
        return (
          <SEOTab
            matchId={matchId || ''}
            matchName={`${currentMatch?.home_team?.name || 'Home Team'} vs ${currentMatch?.away_team?.name || 'Away Team'}`}
            currentMatch={currentMatch}
            onSavingChange={setSaving}
          />
        );
    case 'match-center':
      return <MatchCenterTab matchId={matchId || ''} onSavingChange={setSaving} />;
    case 'api':
      return <APITab matchId={matchId || ''} orgId={orgIdFromPath || ''} onSavingChange={setSaving} />;
    case 'insights':
      return <InsightsTab />;
    case 'gallery':
      return <GalleryTab />;
      default:
        return (
          <SEOTab
            matchId={matchId || ''}
            matchName={`${currentMatch?.home_team?.name || 'Home Team'} vs ${currentMatch?.away_team?.name || 'Away Team'}`}
            currentMatch={currentMatch}
            onSavingChange={setSaving}
          />
        );
    }
  };

  // Format match date
  const formatMatchDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format match time
  const formatMatchTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Consistent Header */}
      <ScoreAppHeader
        sticky={true}
        isMobile={isMobile}
        leftContent={
          // No back button on mobile - use native navigation
          !isMobile ? (
            <Button
              onClick={handleBack}
              variant="ghost"
              size="md"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              }
              iconPosition="left"
            >
              Back to Matches
            </Button>
          ) : null
        }
        centerContent={
          <div className={`flex ${isMobile ? 'space-x-6' : 'space-x-8'} ${isMobile ? 'justify-center' : ''}`}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as any)}
                className={`py-2 px-2 border-b-2 font-medium ${isMobile ? 'text-sm' : 'text-sm'} flex items-center ${isMobile ? 'space-x-1' : 'space-x-2'} min-h-44 ${
                  activeTab === tab.id
                    ? 'border-gray-400 text-gray-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                title={isMobile ? tab.label : undefined}
              >
                <span className={isMobile ? 'text-lg' : ''}>{tab.icon}</span>
                {!isMobile && <span>{tab.label}</span>}
              </button>
            ))}
          </div>
        }
        rightContent={
          <div className="flex items-center space-x-3">
            {activeTab === 'seo' && (
              <Button
                onClick={() => {
                  // Get the SEOTab component's handleSave function
                  const seoTabElement = document.querySelector('[data-seo-tab]');
                  if (seoTabElement) {
                    // Trigger save from SEO tab
                    const event = new CustomEvent('save-seo-data');
                    seoTabElement.dispatchEvent(event);
                  }
                }}
                variant="primary"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg flex items-center space-x-2"
                disabled={saving}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </Button>
            )}
            {activeTab === 'match-center' && (
              <Button
                onClick={() => {
                  const matchCenterElement = document.querySelector('[data-match-center-tab]');
                  if (matchCenterElement) {
                    matchCenterElement.dispatchEvent(new CustomEvent('save-match-center-data'));
                  }
                }}
                variant="primary"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg flex items-center space-x-2"
                disabled={saving}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </Button>
            )}
            {activeTab === 'api' && (
              <Button
                onClick={() => {
                  const apiTabElement = document.querySelector('[data-api-tab]');
                  if (apiTabElement) {
                    apiTabElement.dispatchEvent(new CustomEvent('save-api-data'));
                  }
                }}
                variant="primary"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg flex items-center space-x-2"
                disabled={saving}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </Button>
            )}
          </div>
        }
      />

      {/* Match Card - Exact same as Reports App */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-gradient-to-r from-gray-100 via-gray-200 to-gray-300 rounded-xl p-4 mb-6 text-gray-800 relative overflow-hidden">
          <div className="text-center space-y-3">
            {/* Row 0: Match name (when present) */}
            {(currentMatch.name && currentMatch.name.trim() !== '') && (
              <div className="text-sm font-semibold text-gray-800">
                {currentMatch.name}
              </div>
            )}
            {/* Row 1: Date, Time, Tournament */}
            <div className="flex justify-center items-center space-x-8 text-xs">
              <span className="font-medium">{formatMatchDate(currentMatch.start_date)}</span>
              <span className="font-medium">{formatMatchTime(currentMatch.start_date)}</span>
              <div className="flex items-center space-x-2">
                <img 
                  src={currentMatch.tournament?.logo || 'https://via.placeholder.com/16x16/cccccc/666666?text=🏆'} 
                  alt={`${currentMatch.tournament?.name || 'League'} logo`}
                  className="w-4 h-4 rounded object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/16x16/cccccc/666666?text=🏆';
                  }}
                />
                <span className="font-medium">{currentMatch.tournament?.name || 'League'}</span>
              </div>
            </div>
            
            {/* Row 2: Team A, Score, Team B */}
            <div className="flex justify-center items-center space-x-8">
              <div className="flex items-center space-x-3">
                <img 
                  src={currentMatch.home_team?.logo || 'https://via.placeholder.com/32x32/cccccc/666666?text=🏟️'} 
                  alt={`${currentMatch.home_team?.name || 'Home Team'} logo`}
                  className="w-8 h-8 rounded object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/32x32/cccccc/666666?text=🏟️';
                  }}
                />
                <span className="font-semibold text-lg">{currentMatch.home_team?.name || 'Home Team'}</span>
              </div>
              
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold">{currentMatch.home_score || 0}</span>
                <span className="text-lg font-medium">VS</span>
                <span className="text-3xl font-bold">{currentMatch.away_score || 0}</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <span className="font-semibold text-lg">{currentMatch.away_team?.name || 'Away Team'}</span>
                <img 
                  src={currentMatch.away_team?.logo || 'https://via.placeholder.com/32x32/cccccc/666666?text=🏟️'} 
                  alt={`${currentMatch.away_team?.name || 'Away Team'} logo`}
                  className="w-8 h-8 rounded object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/32x32/cccccc/666666?text=🏟️';
                  }}
                />
              </div>
            </div>
            
            {/* Conditional Row: Either Penalty Shootout OR Venue/Match Number */}
            {currentMatch.home_penalty_score !== undefined && currentMatch.away_penalty_score !== undefined && 
             (currentMatch.home_penalty_score > 0 || currentMatch.away_penalty_score > 0) ? (
              /* Show Penalty Shootout Scores */
              <div className="flex items-center justify-center space-x-2 mt-2 mb-2">
                <span className="text-xs text-gray-500 font-medium">penalties</span>
                <span className="text-xs font-semibold text-gray-600">({currentMatch.home_penalty_score})</span>
                <span className="text-xs text-gray-500">-</span>
                <span className="text-xs font-semibold text-gray-600">({currentMatch.away_penalty_score})</span>
              </div>
            ) : (
              /* Show Venue and Match Number */
              <div className="flex justify-center items-center space-x-8 text-xs">
                <span className="font-medium">{currentMatch.venue || 'TBD'}</span>
                <span className="font-medium">Match #{currentMatch.name || matchId?.slice(-3)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-4">
          {renderTabContent()}
        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
        type={toast.type}
        message={toast.message}
        duration={6000}
      />
    </div>
  );
};

export default MatchDetailPage;
